import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExportIcon from '../icons/ExportIcon';
import DateRangePicker from './DateRangePicker';

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: '20px 16px',
  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #3b7ab8 100%)',
  borderRadius: '12px',
  boxShadow: '0 4px 16px rgba(30, 58, 95, 0.2)',
  color: 'white',
  marginBottom: '16px',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'flex-start',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-100px',
    right: '-50px',
    width: '200px',
    height: '200px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    borderRadius: '50%'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-80px',
    left: '20%',
    width: '150px',
    height: '150px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
    borderRadius: '50%'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '24px 20px',
    marginBottom: '20px',
    borderRadius: '16px',
    gap: '20px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '32px 40px',
    marginBottom: '32px',
    borderRadius: '20px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '24px',
    boxShadow: '0 8px 32px rgba(30, 58, 95, 0.25)',
    '&::before': {
      width: '300px',
      height: '300px'
    },
    '&::after': {
      width: '200px',
      height: '200px'
    }
  }
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    gap: '16px'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    width: '52px',
    height: '52px',
    fontSize: '26px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    width: '56px',
    height: '56px',
    fontSize: '28px',
    borderRadius: '16px'
  }
}));

const TitleSection = styled(Box)({});

const HeaderRight = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '10px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    width: 'auto',
    flexWrap: 'wrap'
  }
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: 'white',
  marginBottom: '4px',
  fontFamily: 'Inter, sans-serif',
  letterSpacing: '-0.3px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '22px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.8)',
  fontFamily: 'Inter, sans-serif',
  maxWidth: '100%',
  lineHeight: 1.4,
  [theme.breakpoints.up('sm')]: {
    fontSize: '13px',
    maxWidth: '400px',
    lineHeight: 1.5
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    maxWidth: '500px'
  }
}));

const ExportButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '10px 16px',
  minHeight: '44px',
  gap: '8px',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  width: '100%',
  justifyContent: 'center',
  WebkitTapHighlightColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.4)'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    minHeight: '40px',
    padding: '10px 18px',
    borderRadius: '10px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      transform: 'translateY(-1px)'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const StatsBadgeCompare = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 14px',
  borderRadius: '10px',
  backgroundColor: 'rgba(251, 191, 36, 0.2)',
  border: '1px solid rgba(251, 191, 36, 0.4)',
  fontSize: '12px',
  fontWeight: 600,
  color: '#FCD34D',
  fontFamily: 'Inter, sans-serif'
}));



const StatsBadge = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '20px',
  background: 'rgba(16, 185, 129, 0.2)',
  border: '1px solid rgba(16, 185, 129, 0.3)',
  fontSize: '12px',
  fontWeight: 600,
  color: '#6ee7b7',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.up('md')]: {
    display: 'flex'
  }
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    minWidth: '200px',
    marginTop: '8px'
  }
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 16px',
  gap: '12px',
  '&:hover': {
    backgroundColor: '#f0f7ff'
  }
}));

const PerformanceHeader = ({ 
  onExportToPDF,
  onShareToMail,
  onShareToWhatsApp,
  selectedTimeRange = '1 Month',
  onTimeRangeChange,
  onCustomDateChange,
  onCompareChange,
  compareEnabled = false,
  comparePeriod = null
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    handleClose();
    onExportToPDF?.();
  };

  const handleShareToMail = () => {
    handleClose();
    onShareToMail?.();
  };

  const handleShareToWhatsApp = () => {
    handleClose();
    onShareToWhatsApp?.();
  };

  return (
    <HeaderContainer>
      <HeaderLeft>
        <IconWrapper>📊</IconWrapper>
        <TitleSection>
          <PageTitle>Performance Dashboard</PageTitle>
          <PageSubtitle>
            Track your Google Business Profile metrics and local search rankings
          </PageSubtitle>
        </TitleSection>
      </HeaderLeft>
      
      <HeaderRight>
        <StatsBadge>
          <span>●</span> Live Data
        </StatsBadge>
        
        {comparePeriod && (
          <StatsBadgeCompare>
            📊 Comparing
          </StatsBadgeCompare>
        )}
        
        <DateRangePicker
          selectedTimeRange={selectedTimeRange}
          onTimeRangeChange={onTimeRangeChange}
          onCustomDateChange={onCustomDateChange}
          onCompareChange={onCompareChange}
          compareEnabled={compareEnabled}
          comparePeriod={comparePeriod}
        />
        
        <ExportButton onClick={handleExportClick}>
          <ExportIcon width={15} height={15} color="white" />
          Export PDF
        </ExportButton>
        
        <StyledMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <StyledMenuItem onClick={handleDownload}>
            <ListItemIcon>
              <DownloadIcon sx={{ color: '#1e3a5f' }} />
            </ListItemIcon>
            <ListItemText primary="Download" primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
          </StyledMenuItem>
          <StyledMenuItem onClick={handleShareToMail}>
            <ListItemIcon>
              <EmailIcon sx={{ color: '#1e3a5f' }} />
            </ListItemIcon>
            <ListItemText primary="Share to Mail" primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
          </StyledMenuItem>
          <StyledMenuItem onClick={handleShareToWhatsApp}>
            <ListItemIcon>
              <WhatsAppIcon sx={{ color: '#25D366' }} />
            </ListItemIcon>
            <ListItemText primary="Share to WhatsApp" primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
          </StyledMenuItem>
        </StyledMenu>
      </HeaderRight>
    </HeaderContainer>
  );
};

export default PerformanceHeader;